import { NavigateBackLink } from '@exam-management/components';
import {
  DetailSectionWrapper,
  GoToParagraphLink,
  LocationFields,
  LocationSectionWrapper,
  QuestionLayout,
} from '@exam-management/qcontent-management';
import {
  ItemSetSubQuestionCreationForm,
  ItemSetSubQuestionCreationFormActions,
} from '@exam-management/question-creation-task-creation';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import {
  PropertiesFormSection,
  useGetQBSLevelAndLevelItemsForTask,
  DetailsFormSection,
  useItemSetQuestionDetails,
  ItemSetSubQuestionInformationSection,
} from '@exam-management/question-creation-task-question-management';
import { Navigate, useParams } from '@exam-management/router';
import { Flex, Loading, PageHeader, PaperSection, Text } from '@shared/components';

export default function TaskManagementQuestionCreationCreateItemSetSubQuestionsPage() {
  const { taskId, questionId } = useParams(
    '/task-management/question-creation/:taskId/item-set/:questionId/create',
  );

  const { task } = useQuestionCreationTaskDetails();
  const { itemSet } = useItemSetQuestionDetails();

  const qBankStructureId = task.metadata?.requirement.qBankStructure?.id ?? '';
  const {
    qbsLevelItems,
    qbsLevels,
    loading: isGettingQBSLevel,
  } = useGetQBSLevelAndLevelItemsForTask(qBankStructureId, task.id);
  const itemSetCreation = task.metadata?.requirement.itemSetCreation?.find(
    (creation) => creation.questionGroupId === questionId,
  );

  if (isGettingQBSLevel) {
    return <Loading sx={{ height: '100%' }} />;
  }
  if ((itemSetCreation?.questionCreationCount ?? 0) >= (itemSetCreation?.size ?? 0)) {
    return (
      <Navigate
        to='/task-management/question-creation/:taskId/item-set/:questionId'
        params={{
          taskId,
          questionId,
        }}
        replace
      />
    );
  }

  const levelItem = qbsLevelItems.filter((item) =>
    itemSet?.questionStructures.find((structure) => structure.refLevelItemId === item.id),
  );

  return (
    <>
      <PageHeader
        title='Add New Question to Item Set'
        subtitleSection={
          <Flex
            sx={{
              gap: (theme) => theme.spacing(4),
              alignItems: 'center',
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            <Text variant='body2'>Task ID: {task.code}</Text>
            <Text variant='body2'>{task.metadata?.requirement.qBankStructure?.name}</Text>
          </Flex>
        }
        linkAdornment={
          <NavigateBackLink
            to='/task-management/question-creation/:taskId/item-set/:questionId'
            params={{ taskId: task.id, questionId }}
          />
        }
      />
      <ItemSetSubQuestionCreationForm qbsLevels={qbsLevels}>
        <QuestionLayout
          sideContent={
            <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
              <PaperSection title='Location'>
                <LocationSectionWrapper>
                  <LocationFields qbsLevels={qbsLevels} qbsLevelItems={levelItem} />
                </LocationSectionWrapper>
              </PaperSection>
            </Flex>
          }
        >
          <Flex sx={{ width: '100%', flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
            <PaperSection title='Question Details'>
              <DetailSectionWrapper>
                <ItemSetSubQuestionInformationSection
                  mode='workflow'
                  titleEndAdornment={
                    <GoToParagraphLink
                      to='/task-management/question-creation/:taskId/item-set/:questionId'
                      params={{ taskId, questionId }}
                    />
                  }
                />
                <DetailsFormSection taskId={task.id} />
                <PropertiesFormSection taskId={task.id} />
              </DetailSectionWrapper>
            </PaperSection>
            <ItemSetSubQuestionCreationFormActions />
          </Flex>
        </QuestionLayout>
      </ItemSetSubQuestionCreationForm>
    </>
  );
}
